import React, { useEffect, useState } from 'react';
import styled, { createGlobalStyle, ThemeProvider } from 'styled-components';
import { Link } from 'react-router-dom';
import ThemeToggleIcon from './ThemeToggleIcon';
import { lightTheme, darkTheme, createSharedComponents } from '../styles/theme';

const { SharedGlobalStyle, SharedBackgroundContainer } = createSharedComponents(styled, createGlobalStyle);

const PageContainer = styled.div`
  min-height: 100svh;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem 1.5rem 3rem;
  box-sizing: border-box;
`;

const TopBar = styled.div`
  width: 100%;
  max-width: 920px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const NavLink = styled(Link)`
  font-weight: 700;
  color: ${props => props.$themeMode === 'light' ? '#5a2c17' : '#fff'};
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;

  &:hover {
    text-decoration: underline;
  }
`;

const Title = styled.h1`
  font-family: 'Bungee', cursive;
  font-size: 2.5rem;
  margin: 0 0 1rem;
  text-align: center;
`;

const Intro = styled.p`
  max-width: 920px;
  font-size: 1.05rem;
  line-height: 1.65;
  margin-bottom: 2rem;
  text-align: center;
`;

const InlineLink = styled(Link)`
  color: ${props => props.$themeMode === 'light' ? '#4B8E8D' : '#F8C471'};
  text-decoration: underline;
  font-weight: 600;

  &:hover {
    opacity: 0.85;
  }
`;

const FAQSection = styled.section`
  width: 100%;
  max-width: 920px;
  background: ${props => props.$themeMode === 'light'
    ? 'rgba(255,255,255,0.85)'
    : 'rgba(0,0,0,0.35)'};
  border-radius: 1.5rem;
  padding: 2rem;
  box-shadow: 0 15px 45px rgba(0,0,0,0.18);
  backdrop-filter: blur(12px);
`;

const FAQItem = styled.article`
  & + & {
    margin-top: 1.5rem;
    padding-top: 1.5rem;
    border-top: 1px solid ${props => props.$themeMode === 'light' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.15)'};
  }
`;

const Question = styled.h2`
  font-size: 1.25rem;
  margin-bottom: 0.4rem;
  font-family: 'Nunito', sans-serif;
`;

const Answer = styled.p`
  margin: 0;
  line-height: 1.7;
  font-family: 'Nunito', sans-serif;
  font-size: 1rem;
`;

const KeywordHighlight = styled.span`
  font-weight: 700;
`;

const faqItems = [
  {
    question: 'How do you play Letter Heist?',
    renderAnswer: (theme) => (
      <>
        Join a lobby, tap tiles on the shared board to reveal letters, and remember what you uncover. Use those letters to build five different five-letter words in any direction. Each correct word gives you a bonus turn, so keep revealing and swapping until you claim all five. Check the{' '}
        <InlineLink to="/" $themeMode={theme}>
          HOW TO PLAY
        </InlineLink>{' '}
        section on the home page for a step-by-step tutorial.
      </>
    ),
  },
  {
    question: 'Is Letter Heist free?',
    renderAnswer: () => (
      <>
        Yes. The game is completely free to play in your browser. Optional cosmetics help fund development but every competitive feature is available at no cost.
      </>
    ),
  },
  {
    question: 'Can I play with friends?',
    renderAnswer: () => (
      <>
        Absolutely. Create a private room, send the invite link, and play real-time rounds with 2–5 players. You all work on the same grid, so you can see where your friends are probing and race to finish words first.
      </>
    ),
  },
  {
    question: 'What makes Letter Heist different from Wordle?',
    renderAnswer: () => (
      <>
        Letter Heist is live, not turn-based. Everyone shares the board, colored markers show the tiles opponents touched, and you can swap any revealed letters (even theirs) to break their plans. The first player to lock in five words wins, so every move matters.
      </>
    ),
  },
  {
    question: 'Does it work on phones and tablets?',
    renderAnswer: () => (
      <>
        Yes. Letter Heist uses a responsive interface so taps, swipes, and drag actions feel smooth on mobile devices. You can start a match on desktop and finish on your phone without losing progress.
      </>
    ),
  },
];

const FaqPage = () => {
  const [theme, setTheme] = useState('light');

  useEffect(() => {
    const savedTheme = localStorage.getItem('gameTheme');
    if (savedTheme === 'dark' || savedTheme === 'light') {
      setTheme(savedTheme);
    }
  }, []);

  const toggleTheme = () => {
    const nextTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(nextTheme);
    localStorage.setItem('gameTheme', nextTheme);
  };

  const themeObject = theme === 'light' ? lightTheme : darkTheme;

  return (
    <ThemeProvider theme={themeObject}>
      <SharedGlobalStyle theme={themeObject} />
      <SharedBackgroundContainer theme={themeObject}>
        <PageContainer>
          <TopBar>
            <ThemeToggleIcon theme={theme} toggleTheme={toggleTheme} />
            <NavLink to="/" $themeMode={theme}>
              ← Back to Letter Heist
            </NavLink>
          </TopBar>
          <Title>Letter Heist FAQ</Title>
          <Intro>
            Your go-to guide for mastering our <KeywordHighlight>multiplayer Wordle strategy game</KeywordHighlight>. Browse quick answers, then jump back into the heist ready to outsmart rival word thieves.
          </Intro>
          <FAQSection $themeMode={theme}>
            {faqItems.map(({ question, renderAnswer }) => (
              <FAQItem key={question} $themeMode={theme}>
                <Question>{question}</Question>
                <Answer>{renderAnswer(theme)}</Answer>
              </FAQItem>
            ))}
          </FAQSection>
        </PageContainer>
      </SharedBackgroundContainer>
    </ThemeProvider>
  );
};

export default FaqPage;

